import { motion, useReducedMotion } from 'motion/react'
import { ArrowUpRight, Play } from 'lucide-react'
import { creator } from '@/config/creator'

type Appearance = {
  videoId: string
  title: string
  titleEn?: string
  englishTitle?: string
  outlet: string
  date: string
  url?: string
  href?: string
}

function formatDate(value: string) {
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat('en', { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(date)
}

export default function FounderRecognition() {
  const reduceMotion = useReducedMotion()
  const appearances = creator.appearances as readonly Appearance[]

  return (
    <section id="appearances" aria-labelledby="appearances-heading" className="creator-section">
      <div id="recognition" className="creator-container min-w-0 scroll-mt-28">
        <motion.div initial={reduceMotion ? false : { opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ duration: reduceMotion ? 0 : 0.5 }}>
          <div className="grid grid-cols-1 items-end gap-6 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <p className="creator-label text-muted-foreground">Interviews & recognition</p>
              <h2 id="appearances-heading" className="creator-display mt-4 max-w-3xl text-4xl sm:text-5xl lg:text-6xl">Conversations beyond the build.</h2>
              <p className="mt-5 max-w-xl text-lg leading-8 text-muted-foreground">I share what I’m learning about entrepreneurship, AI and the future of building software.</p>
            </div>
            <aside className="bento-card bg-accent p-6 lg:col-span-4" aria-label="Fortune recognition">
              <p className="creator-label">Fortune</p>
              <h3 className="creator-display mt-2 text-3xl">Under 40</h3>
              <p className="mt-3 text-base leading-7">I’m proud to have been recognized by Fortune among its Under 40 leaders.</p>
            </aside>
          </div>
          <p id="appearances-help" className="mt-8 text-sm text-muted-foreground">Scroll to explore the conversations. Open any card to watch on YouTube.</p>
          <div tabIndex={0} role="region" aria-label="Video appearances" aria-describedby="appearances-help" className="mt-4 flex max-w-full snap-x snap-mandatory gap-5 overflow-x-auto overscroll-x-contain rounded-3xl pb-5">
            {appearances.map(appearance => (
              <a key={appearance.videoId} href={appearance.url || appearance.href || `https://www.youtube.com/watch?v=${appearance.videoId}`} target="_blank" rel="noopener noreferrer" className="bento-card group flex w-[85%] min-w-0 shrink-0 snap-start flex-col overflow-hidden bg-card sm:w-[46%] lg:w-[calc((100%-2.5rem)/3)] motion-reduce:transform-none">
                <div className="relative">
                  <img src={`https://i.ytimg.com/vi/${appearance.videoId}/hqdefault.jpg`} alt="" width={480} height={360} loading="lazy" decoding="async" className="aspect-video w-full object-cover" />
                  <span aria-hidden="true" className="absolute bottom-4 right-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary text-primary-foreground"><Play size={20} /></span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold text-muted-foreground"><span>{appearance.outlet}</span><time dateTime={appearance.date}>{formatDate(appearance.date)}</time></p>
                  <h3 className="mt-3 font-display text-xl font-bold leading-snug tracking-tight">{appearance.titleEn || appearance.englishTitle || appearance.title}</h3>
                  <span className="mt-auto flex min-h-11 items-center gap-2 pt-5 text-sm font-semibold text-primary">Watch the conversation <ArrowUpRight size={18} aria-hidden="true" /></span>
                </div>
              </a>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
